import { take } from 'rxjs';
import SignaturePad from 'signature_pad';
import * as uuid from 'uuid';

import {
    AfterViewInit, Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output,
    ViewChild
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ScreenOrientation } from '@awesome-cordova-plugins/screen-orientation/ngx';
import { StatusBar } from '@capacitor/status-bar';
import { IonModal, Platform } from '@ionic/angular';
import { BasePage } from '@pages/base.page';
import { CDN_URL } from '@shared/constants';
import { ConfigService } from '@shared/services/core/config.service';
import { FileService } from '@shared/services/core/file.service';
import { CompanyRepository } from '@shared/state/company/company.repository';

function rotate(srcBase64, degrees) {
	const canvas = document.createElement('canvas');
	let ctx = canvas.getContext('2d');
	let image = new Image();
	image.src = srcBase64;

	const promise = new Promise(resolve => {
		image.onload = function () {
			canvas.width = degrees % 180 === 0 ? image.width : image.height;
			canvas.height = degrees % 180 === 0 ? image.height : image.width;

			ctx.translate(canvas.width / 2, canvas.height / 2);
			ctx.rotate((degrees * Math.PI) / 180);
			ctx.drawImage(image, image.width / -2, image.height / -2);

			resolve(canvas.toDataURL());
		};
	});
	return promise;
}

@Component({
	selector: 'signature-modal',
	templateUrl: `signature.component.html`,
	styles: [
		`
			.canvas-container {
				border: 3px dashed gray;
				height: 85vh;
				height: 85dvh;
				border-radius: 5px;
			}

			canvas {
				width: 100%;
				height: 90vh;
				height: 90dvh;
			}
		`,
	],
})
export class SignatureModalComponent extends BasePage implements OnInit, AfterViewInit, OnDestroy {
	public form = new FormGroup({
		consentEnglish: new FormControl(false, [Validators.requiredTrue]),
		consentSignautare: new FormControl(false, [Validators.requiredTrue]),
	});
	signaturePad: any;
	canvasWidth: number = 750;
	canvasHeight: number = 350;
	isLoading: boolean = true;
	disableButtons: boolean = false;

	payload;
	private retries = 0;
	private isDesktop = false;
	@ViewChild('canvas') signaturePadElement;
	@ViewChild('content') content;
	@ViewChild('consentModal') consentModal: IonModal;
	@ViewChild(IonModal) modal: IonModal;
	@Input('showCancellationPolicy') showCancellationPolicy: boolean = false;
	@Input('customMessage') customMessage: string = '';
	@Input('initials') initials: boolean = false;
	@Input('fullscreen') fullscreen: boolean = false;
	@Input('filename') filename: string = uuid.v4();
	@Output() onModalClose: EventEmitter<any> = new EventEmitter();

	/*
	 **-------------------------------------------------------------------------------------
	 ** ME.OD NAME - constructor
	 **-------------------------------------------------------------------------------------
	 */
	constructor(
		public readonly companyRepo: CompanyRepository,
		public readonly platform: Platform,
		private readonly fileService: FileService,
		private readonly screenOrientation: ScreenOrientation,
		private readonly config: ConfigService,
		private readonly fb: FormBuilder
	) {
		super();
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - ngOninit
	 **-------------------------------------------------------------------------------------
	 */
	ngOnInit() {
		if (this.platform.is('hybrid')) {
			this.screenOrientation.lock(this.screenOrientation.ORIENTATIONS.PORTRAIT);
		}
		if (this.platform.is('desktop')) {
			this.isDesktop = true;
		}
	}
	/*
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 ** METHOD NAME - onProcessConsentForm
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 */
	public onProcessConsentForm(): void {
		if (this.form.valid) {
			this.presentSignature();
			return;
		}
		this.alert({
			message: 'Please consent to understanding english and consent to use of electronic signature.',
		});
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - ngOnDestroy
	 **-------------------------------------------------------------------------------------
	 */
	ngOnDestroy(): void {
		this.form = null;
		this.disableButtons = false;
		this.isLoading = false;
		this.signaturePad = null;
		this.payload = null;
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - ngAfterViewInit
	 **-------------------------------------------------------------------------------------
	 */
	public ngAfterViewInit(): void {}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - onResize
	 **-------------------------------------------------------------------------------------
	 */
	@HostListener('window:resize', ['$event'])
	onResize(event) {
		this.init();
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - waitForSignaturePad
	 **-------------------------------------------------------------------------------------
	 */
	waitForSignaturePad(selector) {
		return new Promise(resolve => {
			setTimeout(() => resolve(selector), 500); //ensure elements are on DOM
		});
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - init
	 **-------------------------------------------------------------------------------------
	 */
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - init
	 **-------------------------------------------------------------------------------------
	 */
	async init() {
		try {
			this.signaturePad = new SignaturePad(this.signaturePadElement.nativeElement);
			this.signaturePad.clear();
			this.signaturePad.penColor = 'rgb(56,128,255)';
			this.signaturePad.minWidth = 3;
			this.signaturePad.maxWidth = 8;
			this.signaturePadElement.width = window.innerWidth;
			this.signaturePadElement.height = this.content.el.offsetHeight;

			if (this.signaturePad) {
				this.resizeCanvas();
				this.signaturePad.clear();
			}
		} catch (e) {}
	}

	public resizeCanvas() {
		const ratio = Math.max(window.devicePixelRatio || 1, 1);
		this.signaturePadElement.nativeElement.width = this.signaturePadElement.nativeElement.offsetWidth * ratio;
		this.signaturePadElement.nativeElement.height = this.signaturePadElement.nativeElement.offsetHeight * ratio;
		this.signaturePadElement.nativeElement.getContext('2d').scale(ratio, ratio);
		this.signaturePad.clear();
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - create
	 **-------------------------------------------------------------------------------------
	 */
	public create(payload) {
		this.retries = 0;
		this.payload = payload;
		return this;
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - open
	 **-------------------------------------------------------------------------------------
	 */
	public async present() {
		if (this.platform.is('capacitor') && this.platform.is('tablet')) {
			await StatusBar?.hide();
		}
		this.disableButtons = false;
		this.consentModal.backdropDismiss = false;
		await this.consentModal.present();
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - presentSignature
	 **-------------------------------------------------------------------------------------
	 */
	public async presentSignature() {
		await this.presentLoading('Loading Signature Pad');
		await this.consentModal.dismiss();
		await this.modal.present();
		await this.init();
		await this.dismissAllLoaders();
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - close
	 **-------------------------------------------------------------------------------------
	 */
	public async close() {
		if (this.platform.is('capacitor') && this.platform.is('tablet')) {
			await StatusBar?.show();
		}
		this.signaturePad = undefined;
		await this.modal.dismiss(null, 'close');
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - isCanvasBlank
	 **-------------------------------------------------------------------------------------
	 */
	isCanvasBlank(): boolean {
		if (this.signaturePad) {
			return this.signaturePad.isEmpty() ? true : false;
		}
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - sava
	 **-------------------------------------------------------------------------------------
	 */
	async save() {
		if (this.signaturePad.isEmpty()) return;
		this.disableButtons = true;
		const img = this.signaturePad.toDataURL();
		const trimmedImage = await this.trimSignature(img);
		const rotatedImage = await rotate(trimmedImage, 270);

		this.payload.signature = this.isDesktop ? trimmedImage : rotatedImage;
		this.payload.documentsSigned = true;

		if (this.payload.uploadFile) {
			const fileName = this.filename + '.png';
			this.payload.signatureLocation = `${CDN_URL}/${this.config.get<string>('env').toLowerCase()}/${this.payload.uploadPath}${fileName}`;
			this.fileService
				.uploadFile2({
					rawFile: {
						dataUrl: rotatedImage,
						format: 'png',
					},
					path: this.payload.uploadPath,
					mime: 'image/png',
					fileName,
					isSignature: true,
					extras: await this.fileService.getMetaData(),
				})
				.pipe(take(1))
				.subscribe({
					next: (response: any) => {
						this.onModalClose.emit({ ...this.payload, success: true, Location: response.Location });
						this.close();
					},
					error: () => {
						this.onModalClose.emit({ ...this.payload, success: false });
						this.close();
					},
				});
			return;
		}
		if (!this.payload?.wait) {
			this.onModalClose.emit({ ...this.payload, success: true });
			this.close();
		}
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - clear
	 **-------------------------------------------------------------------------------------
	 */
	clear() {
		this.signaturePad.clear();
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - undo
	 **-------------------------------------------------------------------------------------
	 */
	undo() {
		const data = this.signaturePad.toData();
		if (data) {
			data.pop();
			this.signaturePad.fromData(data);
		}
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - rotoate
	 **-------------------------------------------------------------------------------------
	 */
	rotate90(src) {
		const img = new Image();
		img.src = src;
		const promise = new Promise(resolve => {
			img.onload = () => {
				let canvas = document.createElement('canvas');
				img.height = img.height / 2;
				img.width = img.width / 2;
				canvas.width = img.naturalHeight;
				canvas.height = img.naturalWidth;
				const cw = canvas.width;
				const ch = canvas.height;
				const ctx = canvas.getContext('2d');

				ctx.translate(cw, ch / cw);
				ctx.rotate(Math.PI / 2);
				ctx.drawImage(img, 0, 0);
				ctx.save();
				ctx.restore();
				resolve(canvas.toDataURL());
			};
		});
		return promise;
	}
	/*
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 */
	trimSignature(src) {
		const original = new Image();
		original.src = src;

		const promise = new Promise(resolve => {
			original.onload = () => {
				let trimmedCanvas = document.createElement('canvas');
				let trimmedContext = trimmedCanvas.getContext('2d');

				trimmedCanvas.width = original.width;
				trimmedCanvas.height = original.height;
				trimmedContext.drawImage(original, 0, 0);

				let width = trimmedCanvas.width;
				let height = trimmedCanvas.height;
				let pixels = { x: [], y: [] };
				let imageData = trimmedContext.getImageData(0, 0, trimmedCanvas.width, trimmedCanvas.height);
				let x, y, index;

				for (y = 0; y < height; y++) {
					for (x = 0; x < width; x++) {
						index = y * width + x;
						if (imageData.data[index * 4 + 3] > 0) {
							pixels.x.push(x);
							pixels.y.push(y);
						}
					}
				}
				pixels.x.sort(function (a, b) {
					return a - b;
				});
				pixels.y.sort(function (a, b) {
					return a - b;
				});
				let n = pixels.x.length - 1;

				width = pixels.x[n] - pixels.x[0];
				height = pixels.y[n] - pixels.y[0];
				let trimmed = trimmedContext.getImageData(pixels.x[0], pixels.y[0], width, height);

				trimmedCanvas.width = width;
				trimmedCanvas.height = height;
				trimmedContext.putImageData(trimmed, 0, 0);

				resolve(trimmedCanvas.toDataURL());
			};
		});

		return promise;
	}
	/*
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 */
	replaceSignaturePlaceholders(doc) {
		const signatureToken = '$signature$';
		const initialsToken = '$initials$';
		const signatureRegex = new RegExp(signatureToken.replace(/[/\-\\^$*+?.()|[\]{}]/g, '\\$&'), 'g');
		const initialsRegex = new RegExp(initialsToken.replace(/[/\-\\^$*+?.()|[\]{}]/g, '\\$&'), 'g');

		return doc.replace(signatureRegex, '____________________').replace(initialsRegex, '____________________');
	}
}
